import $ from 'jQuery';
// @ts-ignore
import { transformToTitleCase } from '../../../helpers';
import './base_components.css';
import icons from '../../../icons';
import tooltips from '../../machinelogic_tooltips';

/**
 * The ReactiveMLComponent is a framework class for easily updating a piece of the UI based on changes in the data.
 * It allows changes in the data to directly impact the UI by having listeners trigger a re-render of the component with the most recent state.
 *
 * The lifecycle of a ReactiveMLComponent is dictated by 3 parts --> The container, the render function, and listeners.
 *
 * --- The Container ---
 * - Holds all the content of the component
 * - Stays on the DOM for the entire lifecylce of the component.
 * - On render() call, it is emptied and refilled with new content that reflects the current state
 *
 * --- render() ---
 * - Recreates the content of the component so that it reflects the most recent state.
 * - Should be called every time there is a relevant change in state.
 *
 * --- Listeners ---
 * - A listener is triggered any time a relevent change in state occuts.
 * - They are instantiated in the constructor, and removed in the cleanup function.
 * - When triggered, a listener should always call the ReactiveMLComponent.render() function to update the component with the most recent state
 */
class ReactiveMLComponent {

  /**
   * @typedef {Object} ReactiveMLComponentState
   * @prop {(state: object, render: Function?) => JQuery<HTMLElement> | JQuery<HTMLElement>[] | []} createElement
   * @prop {JQuery<HTMLElement>} container
   * @prop {*} state
   */
  constructor({ createElement, container, state }) {
    this.mCreateElement = createElement;
    this.mState = state;
    this.mContainer = container;

    this.mContainer.on("remove", () => {
      this.cleanUp();
    });
  }

  /**
   * Creates a new render of the component inside of the container using the current state values.
   * Returns the container so it can be added to the DOM (only necessary on first render)
   * @returns {JQuery<HTMLElement>} Container
   */
  render = () => {
    this.mContainer.empty();
    const lCreatedElems = this.mCreateElement(this.mState, this.render);
    this.mContainer.append(lCreatedElems);
    return this.mContainer;
  };

  /**
   * The cleanUp function should perform any necessary actions when the component is removed from the DOM (ex. remove listeners or notifications).
   * Override this method on any class that extends ReactiveMLComponent
   */
  cleanUp = () => {
    console.warn("No cleanup override on ReactiveMLComponent. Make sure you aren't leaving any listeners behind.");
  };
}


/**
 * A wrapper around an HTML select option with machinelogic styles
 * @param {string} pValue
 * @param {string} pText
 * @param {boolean} pDisabled
 * @param {boolean} [pSelected]
 * @returns {JQuery<HTMLElement>}
 */
const selectOption = (pValue, pText, pDisabled, pSelected) => $('<option>').attr({ value: pValue, disabled: pDisabled, selected: pSelected }).text(pText);

/**
 * * A wrapper around an HTML select option group with machinelogic styles
 * @param {string} pTitle
 * @param {string} pLabel
 * @param {JQuery<HTMLElement>[]} pOptions
 * @returns {JQuery<HTMLElement>}
 */
const selectOptionGroup = (pTitle, pLabel, pOptions) => {
  const lSubgroup = $('<optgroup>').attr({ title: pTitle, label: pLabel });
  lSubgroup.append(pOptions);
  return lSubgroup;
};

/**
 * A wrapper around a HTML select widget with machinelogic styles
 * @param {string | null} pName
 * @param {JQuery<HTMLElement> | JQuery<HTMLElement>[]} pSelectOptions
 * @param {string | undefined} pInitValue
 * @param {string | undefined} pTooltip
 * @param {(newVal: string) => void} pOnChange
 * @param {Object} [pOptions]
 * @param {string} [pOptions.selectDivClass]
 * @param {string} [pOptions.tooltipKey] key to use to get the tooltip text
 * @returns {{
 *   widget: JQuery<HTMLElement>,
 *   getSelect: Function
 * }}
 */
const selectComponent = (pName, pSelectOptions, pInitValue, pTooltip, pOnChange, {selectDivClass = 'ml_select_component', tooltipKey = undefined} = {}) => {
  const lWidget = baseComponent(pName, { tooltipKey } );
  const lSelectContainer = $('<div>').addClass(selectDivClass);
  const lSelect = $('<select>').attr({ title: pTooltip });

  lSelect.append(pSelectOptions);
  pInitValue !== undefined && lSelect.val(pInitValue);
  // @ts-ignore
  lSelect.change((event) => pOnChange(event.target.value));

  lWidget.append(
    lSelectContainer.append(lSelect)
  );

  return {
    widget: lWidget,
    getSelect: () => lSelect,
    getSelectContainer: () => lSelectContainer
  };
};


/**
 * Returns a bare bones machinelogic config card with a label.
 * @param {string} pName The name of the config card components
 * @param {Function} pOnClose The function to call when the config card is removed
 * @param {boolean} [isOnPendant] optional - uses a different class name instead .ml_config_card becomes .ml_config_card_pendant. This gives better control over child elements that differ between the cad and the edge.
 * @returns {{
 *   widget: JQuery<HTMLElement>,
 *   updateContents: Function
 * }}
 */
const configCardComponent = (pName, pOnClose, isOnPendant) => {
  const lWidget = $('<div>').addClass(isOnPendant ? 'ml_config_card_pendant' : 'ml_config_card');
  const lCloseButton = $('<button>').addClass('ml_config_card_close_button').text('x');
  const lLabel = $('<div>').addClass("ml_config_card_label").append($('<div>').text(pName));
  const lContent = $('<div>').addClass('ml_config_card_content');

  // @ts-ignore
  lCloseButton.on('click', pOnClose);
  lWidget.append(lLabel, lContent, lCloseButton);

  return {
    widget: lWidget,
    updateContents: (...pContents) => lContent.append(...pContents)
  };
};

/**
 * A base widget component that must be used by other widget to preserve the ml theme.
 * @param {string | null} [pLabelText] The label of the widget
 * @param {Object} [pOptions]
 * @param {string} [pOptions.tooltipKey] key to use to get the tooltip text
 * @returns {JQuery<HTMLElement>}
 */
const baseComponent = (pLabelText, {tooltipKey = undefined} = {}) => {
  const lWidget = $('<div>').addClass('v_layout spaced');
  if (pLabelText) {
    const lLabel = $('<h5>').addClass('ml_component_label').text(transformToTitleCase(pLabelText));
    lWidget.append(lLabel);
    if(tooltipKey) {
      lLabel.append(
        $('<span>').addClass(`ml_tooltip_icon ml_label_icon ${icons.unknown}`).on( 'click', function () { tooltips.showTips(tooltipKey); })
      );
    }
  }
  return lWidget;
};

export {
  ReactiveMLComponent,
  selectOption,
  selectOptionGroup,
  selectComponent,
  configCardComponent,
  baseComponent
};
